/**
 * Soft Pass 角标组件
 * 对于低置信度但放行的情况，显示一个不打断流程的小角标
 * 基于"最小打扰"设计原则
 *
 * @see Requirements 1.3
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface SoftPassBadgeProps {
  /** 是否显示 */
  visible: boolean;
  /** 提示消息 */
  tooltipMessage: string;
  /** 自定义类名 */
  className?: string;
}

/**
 * Soft Pass 角标组件
 * 位置：图片右上角，删除按钮左侧
 * 行为：hover 时显示 Tooltip
 * 目的：不打断流程，但保留可追溯性
 */
export const SoftPassBadge: React.FC<SoftPassBadgeProps> = ({
  visible,
  tooltipMessage,
  className,
}) => {
  if (!visible) {
    return null;
  }

  return (
    <AnimatePresence>
      {visible && (
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn("absolute top-3 right-12 z-10", className)}
              data-testid="soft-pass-badge"
            >
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-full bg-black/80 backdrop-blur-sm text-white hover:bg-yellow-50 hover:text-yellow-700 dark:hover:bg-yellow-950 shadow-md cursor-default"
                tabIndex={-1}
                type="button"
              >
                <TriangleAlert className="h-4 w-4" />
              </Button>
            </motion.div>
          </TooltipTrigger>
          <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
        </Tooltip>
      )}
    </AnimatePresence>
  );
};

export default SoftPassBadge;
